import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocale, formatNumber as formatNumberLocale } from "@/i18n/format";
import { useBrandContext } from "@/contexts/brand-context";
import {
  TimeRangeSelector,
  type AnalyticsRange,
} from "@/components/analytics/time-range-selector";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { Youtube, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { RetentionCurveChart } from "@/components/analytics/retention-curve-chart";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const BASE = "/api";

interface YouTubeVideoRow {
  videoId: string;
  scheduledPostId: string | null;
  pieceTitle: string | null;
  totalViews: string;
  avgViewPercentage: number | null;
  subsGained: string;
  durationSeconds: number | null;
  lastDate: string;
}

interface YouTubeDailyRow {
  date: string;
  views: string;
  estimatedMinutesWatched: string;
  averageViewDuration: number | null;
  averageViewPercentage: number | null;
  subscribersGained: string;
  subscribersLost: string;
  likes: string;
  comments: string;
  shares: string;
}

interface YouTubeRetentionPoint {
  elapsedVideoTimeRatio: number;
  audienceWatchRatio: number;
  relativeRetentionPerformance?: number;
}

interface YouTubeRetentionResponse {
  videoId: string;
  title: string | null;
  durationSeconds: number | null;
  retentionCurve: YouTubeRetentionPoint[];
}

interface YouTubeAccountStatus {
  id: string;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
}

async function fetchJson<T>(url: string): Promise<T | null> {
  const r = await fetch(url, { credentials: "include" });
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`Request failed: ${url}`);
  return r.json() as Promise<T>;
}

function useYtVideos(brandId: string, days: AnalyticsRange) {
  return useQuery({
    queryKey: ["yt-videos", brandId, days],
    queryFn: () =>
      fetchJson<{ videos: YouTubeVideoRow[] }>(
        `${BASE}/brands/${brandId}/analytics/youtube/videos?days=${days}`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

function useYtDailyViews(brandId: string, videoId: string | null, days: AnalyticsRange) {
  return useQuery({
    queryKey: ["yt-daily-views", brandId, videoId, days],
    queryFn: () =>
      fetchJson<{ daily: YouTubeDailyRow[] }>(
        `${BASE}/brands/${brandId}/analytics/youtube/videos/${videoId}/daily-views?days=${days}`,
      ),
    enabled: !!videoId,
    staleTime: 5 * 60 * 1000,
  });
}

function useYtRetention(brandId: string, videoId: string | null) {
  return useQuery({
    queryKey: ["yt-retention", brandId, videoId],
    queryFn: () =>
      fetchJson<YouTubeRetentionResponse>(
        `${BASE}/brands/${brandId}/analytics/youtube/videos/${videoId}/retention`,
      ),
    enabled: !!videoId,
    staleTime: 5 * 60 * 1000,
  });
}

function useYtAccountStatus(brandId: string) {
  return useQuery({
    queryKey: ["yt-account-status", brandId],
    queryFn: () =>
      fetchJson<YouTubeAccountStatus>(
        `${BASE}/brands/${brandId}/analytics/youtube/account-status`,
      ),
    staleTime: 5 * 60 * 1000,
  });
}

function formatYtNumber(n: string | number | null | undefined, locale: string): string {
  const num = typeof n === "string" ? Number(n) : n;
  if (num == null || !Number.isFinite(num)) return "—";
  return formatNumberLocale(num, locale);
}

function VideosTable({
  videos,
  selectedVideoId,
  onSelect,
  locale,
}: {
  videos: YouTubeVideoRow[];
  selectedVideoId: string | null;
  onSelect: (videoId: string) => void;
  locale: string;
}) {
  const { t } = useTranslation("analytics");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-xs text-muted-foreground uppercase tracking-wide text-left">
            <th className="py-2 pr-3 font-normal">{t("youtube.videos")}</th>
            <th className="py-2 pr-3 font-normal">{t("youtube.totalViews")}</th>
            <th className="py-2 pr-3 font-normal">{t("youtube.avgViewPct")}</th>
            <th className="py-2 pr-3 font-normal">{t("youtube.subsGained")}</th>
          </tr>
        </thead>
        <tbody>
          {videos.map((v) => (
            <tr
              key={v.videoId}
              onClick={() => onSelect(v.videoId)}
              className={cn(
                "border-t border-border cursor-pointer hover:bg-accent",
                selectedVideoId === v.videoId && "bg-accent",
              )}
            >
              <td className="py-2 pr-3 max-w-[280px] truncate" title={v.pieceTitle ?? v.videoId}>
                {v.pieceTitle ?? v.videoId}
              </td>
              <td className="py-2 pr-3 tabular-nums">{formatYtNumber(v.totalViews, locale)}</td>
              <td className="py-2 pr-3 tabular-nums">
                {v.avgViewPercentage != null ? `${v.avgViewPercentage.toFixed(1)}%` : "—"}
              </td>
              <td className="py-2 pr-3 tabular-nums">{formatYtNumber(v.subsGained, locale)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DailyViewsChart({ daily }: { daily: YouTubeDailyRow[] }) {
  const { t } = useTranslation("analytics");
  if (daily.length === 0) {
    return <div className="text-sm text-muted-foreground py-6 text-center">{t("youtube.empty")}</div>;
  }
  const data = daily.map((d) => ({
    date: d.date,
    views: Number(d.views),
    minutesWatched: Number(d.estimatedMinutesWatched),
  }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <LineChart data={data}>
        <XAxis dataKey="date" fontSize={10} />
        <YAxis fontSize={10} />
        <Tooltip />
        <Line type="monotone" dataKey="views" name={t("youtube.dailyViews")} stroke="#ef4444" dot={false} />
        <Line
          type="monotone"
          dataKey="minutesWatched"
          name={t("youtube.minutesWatched")}
          stroke="#3b82f6"
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function RetentionChart({ retention }: { retention: YouTubeRetentionResponse }) {
  const { t } = useTranslation("analytics");
  const duration = retention.durationSeconds ?? 0;
  // x = elapsedVideoTimeRatio * durationSeconds (seconds); y = audienceWatchRatio * 100 (%).
  // audienceWatchRatio can legitimately exceed 1.0 (rewinds/looping, common on
  // Shorts) — never clamp it, even though it renders past 100 on the y-axis.
  const points = retention.retentionCurve.map((p) => ({
    seconds: p.elapsedVideoTimeRatio * duration,
    watchPct: p.audienceWatchRatio * 100,
  }));
  return (
    <RetentionCurveChart
      points={points}
      durationSeconds={duration}
      emptyLabel={t("youtube.empty")}
    />
  );
}

const DAYS_VALUES: AnalyticsRange[] = [7, 30, 90];
function parseDays(raw: string | null): AnalyticsRange {
  const n = Number(raw);
  return (DAYS_VALUES as number[]).includes(n) ? (n as AnalyticsRange) : 30;
}

export function BrandAnalyticsYouTubePage() {
  const { t: tl } = useTranslation("analytics");
  const locale = useLocale();
  const { brand } = useBrandContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const days = parseDays(searchParams.get("days"));
  const setDays = useCallback(
    (v: AnalyticsRange) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (v === 30) next.delete("days");
          else next.set("days", String(v));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  const { data, isLoading, isError } = useYtVideos(brand.id, days);
  const dailyViews = useYtDailyViews(brand.id, selectedVideoId, days);
  const retention = useYtRetention(brand.id, selectedVideoId);
  const accountStatus = useYtAccountStatus(brand.id);

  if (isLoading) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full">
        <EmptyState
          icon={AlertCircle}
          title={tl("youtube.loadError")}
          description={tl("youtube.tryRefresh")}
        />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="px-6 py-8 max-w-7xl mx-auto w-full">
        <EmptyState
          icon={Youtube}
          title={tl("youtube.noAccount")}
          description={tl("youtube.noAccountDesc")}
        />
      </div>
    );
  }

  const videos = data.videos ?? [];
  const scopeMissing = accountStatus.data?.lastSyncError === "youtube_scope_missing";

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{tl("youtube.title")}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">{tl("youtube.subtitle")}</p>
        </div>
        <TimeRangeSelector value={days} onChange={setDays} />
      </div>

      {scopeMissing && (
        <Card className="p-3 text-sm bg-amber-50 border-amber-200 text-amber-900">
          {tl("youtube.scopeMissing")}
        </Card>
      )}

      <Card className="p-4 space-y-3">
        {videos.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">{tl("youtube.empty")}</div>
        ) : (
          <VideosTable
            videos={videos}
            selectedVideoId={selectedVideoId}
            onSelect={setSelectedVideoId}
            locale={locale}
          />
        )}
      </Card>

      {selectedVideoId && (
        <>
          <Card className="p-4 space-y-3">
            <div className="text-sm font-medium">{tl("youtube.dailyViews")}</div>
            {dailyViews.isLoading ? (
              <Skeleton className="h-56 rounded-lg" />
            ) : dailyViews.isError ? (
              <div className="text-sm text-destructive py-6 text-center">{tl("youtube.loadError")}</div>
            ) : (
              <DailyViewsChart daily={dailyViews.data?.daily ?? []} />
            )}
          </Card>

          <Card className="p-4 space-y-3">
            <div>
              <div className="text-sm font-medium">{tl("youtube.retention")}</div>
              <p className="text-xs text-muted-foreground">{tl("youtube.retentionHint")}</p>
            </div>
            {retention.isLoading ? (
              <Skeleton className="h-56 rounded-lg" />
            ) : retention.isError || !retention.data ? (
              <div className="text-sm text-muted-foreground py-6 text-center">{tl("youtube.empty")}</div>
            ) : (
              <RetentionChart retention={retention.data} />
            )}
          </Card>
        </>
      )}
    </div>
  );
}
